import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BeatLoader } from "react-spinners";
import { LuPlus } from "react-icons/lu";

import {
  deleteCategoryByAdmin,
  getAllCategoriesByAdmin,
} from "../services/categoryService";
import { Table } from "../components/table/table";
import { ICategory } from "../types";
import EditCategoryModal from "../components/modals/edit-category";
import DeleteCategoryModal from "../components/modals/delete-category";
import AddCategoryModal from "../components/modals/add-category";
import CategoryDropdownActions from "../components/actions/category-dropdown";

interface CategoriesProps {
  searchQuery?: string;
}

const Categories: React.FC<CategoriesProps> = ({ searchQuery }) => {
  const queryClient = useQueryClient();

  const {
    data: categories,
    isLoading,
    error,
  } = useQuery<ICategory[]>({
    queryKey: ["categories", searchQuery],
    queryFn: () => getAllCategoriesByAdmin(searchQuery),
  });

  const [editCategory, setEditCategory] = useState<ICategory | null>(null);
  const [deleteCategoryData, setDeleteCategoryData] =
    useState<ICategory | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (categoryId: string) => deleteCategoryByAdmin(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setDeleteCategoryData(null);
    },
  });

  const handleEdit = (category: ICategory) => {
    setEditCategory(category);
  };

  const handleDelete = (category: ICategory) => {
    setDeleteCategoryData(category);
  };

  const confirmDelete = () => {
    if (deleteCategoryData) {
      deleteMutation.mutate(deleteCategoryData._id);
    }
  };

  const LuPlusIcon = LuPlus as React.ElementType;

  const columns = [
    {
      header: "Category Name",
      accessorKey: "name",
      cell: ({ row }: { row: { original: ICategory } }) => (
        <p className="text-sm font-medium text-gray-800">{row.original.name}</p>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }: { row: { original: ICategory } }) => (
        <p className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </p>
      ),
    },
    {
      header: "Is Featured",
      accessorKey: "isFeatured",
      cell: ({ row }: { row: { original: ICategory } }) => (
        <p
          className={`text-sm font-medium ${
            row.original.isFeatured ? "text-emerald-500" : "text-rose-500"
          }`}
        >
          {row.original.isFeatured ? "True" : "False"}
        </p>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: ICategory } }) => (
        <CategoryDropdownActions
          category={row.original}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return <p className="text-rose-500 text-sm">Error loading categories.</p>;

  return (
    <div>
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Categories</h2>
        <div
          onClick={() => setShowAddModal(true)}
          className="p-2 cursor-pointer bg-gray-800 rounded-full flex items-center justify-center text-white hover:bg-opacity-50 transition"
        >
          <LuPlusIcon className="text-lg" />
        </div>
      </div>
      <Table columns={columns} data={categories || []} />

      {editCategory && (
        <EditCategoryModal
          category={editCategory}
          onClose={() => setEditCategory(null)}
        />
      )}
      {showAddModal && (
        <AddCategoryModal onClose={() => setShowAddModal(false)} />
      )}
      {deleteCategoryData && (
        <DeleteCategoryModal
          isOpen={!!deleteCategoryData}
          categoryName={deleteCategoryData.name}
          onClose={() => setDeleteCategoryData(null)}
          onConfirm={confirmDelete}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default Categories;
